import { componentSchema } from '@Schema/componentsSchemas';
import { z } from 'zod';
import { PAGE_PATHS } from '../types';
import {
  firstBarBreakdownSuffix,
  firstMultiLineSuffix,
  firstPieBreakdownSuffix,
  secondBarBreakdownSuffix,
  secondMultiLineSuffix,
  secondPieBreakdownSuffix,
} from '../views/Policies/AssetInventoryDashboard/utils';
import {
  mergeZodSchemas,
  transformToDateArray,
  transformToNumber,
  transformToObject,
  transformToString,
  transformToStringArray,
} from './utils';

type SchemaKey = keyof typeof PAGE_PATHS | 'VISUAL_EXPLORER';
export const schema: Record<SchemaKey, z.ZodObject<any>> = {
  LOGS: mergeZodSchemas(
    z.object({
      tabId: transformToString,
      query: transformToString,
      page: transformToString,
      title: transformToString,
      lastSearch: transformToDateArray,
      queryType: transformToString,
      date: transformToDateArray,
      categoryId: transformToString,
      description: transformToString,
    })
  ),
  CONTENT_PERMISSIONS: mergeZodSchemas(componentSchema.AvTableSchema),
  MODEL_MANAGEMENT: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Runs),
  DATA_UNIFICATION_FIELDS: mergeZodSchemas(),
  ALL_ACCOUNTS: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.oltpPagination),
  EDIT_MY_ACCOUNT_MANAGEMENT: mergeZodSchemas(),
  USER_MANAGEMENT: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.oltpPagination),
  ACCOUNT_USAGE: mergeZodSchemas(),
  ALL_USERS: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.oltpPagination),
  EDIT_MY_USER: mergeZodSchemas(),
  GLOBAL_SCRIPTS: mergeZodSchemas(componentSchema.AvTableSchema),
  ALERTS: mergeZodSchemas(componentSchema.AvTableSchema),
  TARGETS: mergeZodSchemas(componentSchema.Reports),
  RULES: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Runs),
  SOURCES: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Runs, componentSchema.oltpPagination),
  TICKETS: mergeZodSchemas(componentSchema.AvTableSchema),
  INCIDENTS: mergeZodSchemas(componentSchema.AvTableSchema),
  INCIDENT_LIST: mergeZodSchemas(componentSchema.AvTableSchema),
  FINDINGS: mergeZodSchemas(componentSchema.AvTableSchema),
  ASSETS: mergeZodSchemas(componentSchema.AvTableSchema),
  WORKFLOWS: mergeZodSchemas(),
  QUERY: mergeZodSchemas(),
  SETTINGS: mergeZodSchemas(),
  RULE_SET: mergeZodSchemas(),
  SEVERITY_SLA: mergeZodSchemas(),
  SEVERITY: mergeZodSchemas(),
  SLA: mergeZodSchemas(),
  TICKETS_STATUSES: mergeZodSchemas(),
  UI_CONFIG: mergeZodSchemas(),
  ENTITIES: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.EntityExplorer),
  LOOKUP_TABLES: mergeZodSchemas(componentSchema.AvTableSchema),
  REMEDIATION: mergeZodSchemas(
    componentSchema.AvTableSchema,
    z.object({
      startDate: transformToString,
      analyze: transformToStringArray,
      keyMetrics: transformToStringArray,
      dateGap: transformToStringArray,
      dateObject: transformToStringArray,
      dateDimPeriod: transformToStringArray,
    })
  ),
  RISK: mergeZodSchemas(
    z.object({
      scatterBy: transformToString,
      method: transformToString,
      keyMetrics: transformToStringArray,
    })
  ),
  ASSET_COVERAGE: mergeZodSchemas(
    componentSchema.AvTableSchema,
    z.object({
      page: transformToNumber,
      seriesIndex: transformToStringArray,
      selected: transformToNumber,
      headCells: transformToStringArray,
    })
  ),
  PIVOT: mergeZodSchemas(componentSchema.AvTableSchema),
  ROI: mergeZodSchemas(),
  THREAT_DASHBOARD: mergeZodSchemas(),
  OVERVIEW: mergeZodSchemas(),
  SCORE: mergeZodSchemas(),
  TEST_QUERY: mergeZodSchemas(),
  REPORTS: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Reports, componentSchema.Runs),
  BACKOFFICE_ACTIONS: mergeZodSchemas(),
  CUSTOM_DASHBOARDS: mergeZodSchemas(
    componentSchema.AvTableSchema,
    componentSchema.ClickthroughSchema,
    componentSchema.Runs,
    componentSchema.Dashboards
  ),
  ALIASES: mergeZodSchemas(componentSchema.AvTableSchema),
  RELATIONS: mergeZodSchemas(componentSchema.AvTableSchema),
  PERMISSIONS: mergeZodSchemas(),
  ROLES: mergeZodSchemas(componentSchema.AvTableSchema),
  SOURCES_MAPPING: mergeZodSchemas(),
  MEASUREMENTS: mergeZodSchemas(),
  FACTOR: mergeZodSchemas(componentSchema.AvTableSchema),
  FACTOR_RULES: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Runs),
  GLOBAL_FACTOR_RULES: componentSchema.AvTableSchema,
  ICONS: mergeZodSchemas(),
  POLICY_COMPLIANCE: mergeZodSchemas(
    componentSchema.AvTableSchema,
    componentSchema.ClickthroughSchema,
    z.object({
      dateGap: transformToString,
      dateObject: transformToObject({
        relative: transformToObject({ periodBehaviour: transformToString, value: transformToNumber, unit: transformToString }),
        between: transformToObject({ values: transformToDateArray }),
      }),
    })
  ),
  ASSET_INVENTORY: mergeZodSchemas(
    componentSchema.AvTableSchema,
    componentSchema.ClickthroughSchema,
    z.object({
      dateGap: transformToString,
      dateObject: transformToObject({
        relative: transformToObject({
          periodBehaviour: transformToString,
          value: transformToNumber,
          unit: transformToString,
        }),
        between: transformToObject({ values: transformToDateArray }),
      }),
      [firstPieBreakdownSuffix]: transformToString,
      [secondPieBreakdownSuffix]: transformToString,
      [firstBarBreakdownSuffix]: transformToString,
      [secondBarBreakdownSuffix]: transformToString,
      [firstMultiLineSuffix]: transformToString,
      [secondMultiLineSuffix]: transformToString,
    })
  ),
  POLICY_VIOLATIONS: mergeZodSchemas(),
  POLICIES: mergeZodSchemas(componentSchema.AvTableSchema, componentSchema.Runs),
  DATA_UNIFICATION: mergeZodSchemas(),
  DATA_UNIFICATION_ENTITIES: mergeZodSchemas(componentSchema.AvTableSchema),
  DATA_UNIFICATION_ENTITY_MERGE: mergeZodSchemas(),
  ASSET_AGING: mergeZodSchemas(),
  DATA_PURGE: mergeZodSchemas(componentSchema.Runs),
  AUTHENTICATION_SOURCES: mergeZodSchemas(),
  EXCEPTIONS: mergeZodSchemas(componentSchema.AvTableSchema),
  EXCEPTIONS_SETTINGS: mergeZodSchemas(),
  FORMATTING_RULES: mergeZodSchemas(),
  RISK360_BACKOFFICE_ACTIONS: mergeZodSchemas(),
  TOOL_COVERAGE_AND_GAP: mergeZodSchemas(
    componentSchema.AvTableSchema,
    componentSchema.ClickthroughSchema,
    z.object({ vennSources: transformToStringArray, coverageGapsSource: transformToString, lastDaysNumber: transformToNumber })
  ),
  VISUAL_EXPLORER: mergeZodSchemas(componentSchema.VisualExplorer),
  VIOLATION_TICKETS: mergeZodSchemas(),
  EXCEPTIONS_STATUSES: mergeZodSchemas(),
  TICKET_LIFECYCLE: mergeZodSchemas(),
  AUTOMATIONS: mergeZodSchemas(),
  ALERT_SCORE: mergeZodSchemas(),
  INCIDENT_RULES: mergeZodSchemas(componentSchema.AvTableSchema),
  SCANNERS_ACTIVATION: mergeZodSchemas(componentSchema.AvTableSchema),
  TENANTS: mergeZodSchemas(componentSchema.AvTableSchema),
  IDENTITY_FINDINGS: mergeZodSchemas(componentSchema.AvTableSchema),
  USERS: mergeZodSchemas(componentSchema.AvTableSchema),
  ITDR_CONFIGURATION: mergeZodSchemas(),
  EDIT_CUSTOM_ROLES: mergeZodSchemas(componentSchema.EditCustomRoles, componentSchema.AvTableSchema),
  EASM_SETTINGS: mergeZodSchemas(),
};
